/**
 * The V3 side of the fork in App.tsx `RootRoutes` (plan §4.8.2).
 *
 * ENTRY STUB: imported statically by App.tsx, so it stays tiny. V3App and the
 * switch bar are lazy chunks (`v3-*`) that V2 users never download (REG-3; R2: the bar's slot comes with the switch's
 * network half, switch/barHost.tsx, so a build without the V3 API carries no bar at all).
 * Their error boundary lives inside those chunks (shell/V3Boundary.tsx, P19b);
 * a chunk that fails to load renders the fallback from here.
 */
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useSwitchBar, v3Telemetry } from './uiVariant';

/**
 * Stripe's return on /upgrade comes off the URL as soon as V3 is chosen. V2's UpgradePage (entry
 * chunk) strips it on mount; V3's route is two lazy chunks later, and that later replaceState made the
 * Google tag send an extra page_view of the success URL (AN-2 with the real tags). V2's rule: an
 * activation return (from_order / from_manual without credits) keeps its URL.
 * UX batch #4: V3's checkouts return to /upgrade; a checkout started before the rename returns to V2's /subscription,
 * which is held the same way (its path kept: the legacy redirect then moves it to /upgrade, where the route reads it).
 */
let heldReturn: string | null = null;
const UPGRADE_PATHS = ['/upgrade', '/subscription', '/subscriptions'];
function holdStripeReturn(): void {
  const { pathname, search } = window.location;
  const p = new URLSearchParams(search);
  const ret = p.get('canceled') === 'true' || p.get('update_card') === 'true' || p.get('success') === 'true';
  const activating = p.get('success') === 'true' && !p.get('credits') && !!(p.get('from_order') || p.get('from_manual'));
  if (!UPGRADE_PATHS.includes(pathname) || heldReturn !== null || !ret || activating) return;
  heldReturn = search;
  window.history.replaceState({}, '', pathname);
}

/** /upgrade's arrival parameters: the held Stripe return (until consumed), else the live URL. */
export function subscriptionArrival(consume = false): URLSearchParams {
  const p = new URLSearchParams(heldReturn ?? window.location.search);
  if (consume) heldReturn = null;
  return p;
}

/** V2 takes over: hand it the Stripe return V3 never handled (on V2's own /subscription). */
export function v3Fallback(): void {
  if (heldReturn !== null) window.history.replaceState({}, '', `/subscription${heldReturn}`);
  heldReturn = null;
}

type FallbackProps = { fallback: ReactNode };

/**
 * A V3 chunk that cannot load shows the fallback (V2App, or nothing for the bar) instead of a blank page. The console line
 * and the V3Crash report are made by the switch chunk (switch/remote.ts), which leaves both out when the failure is the
 * page being left (QA-Z: WebKit / Firefox abort a chunk's CSS or script when a navigation starts). REG-3: fewer bytes
 * in V2's entry than the inline console.error it replaces.
 */
const failed = (error: unknown) => {
  v3Fallback();
  v3Telemetry('V3Crash', { error }); // (the report's message is made by the switch chunk)
  return { default: (p: FallbackProps) => p.fallback as JSX.Element }; // an element or null: what React renders
};
const V3App = lazy(() => import('./V3App').catch(failed));

/** Renders V3App, falling back to `fallback` (V2App) on any V3 error. */
export function V3Fork({ fallback }: FallbackProps) {
  useEffect(holdStripeReturn, []);
  return (
    <Suspense fallback={null}>
      <V3App fallback={fallback} />
    </Suspense>
  );
}

/**
 * The V2-side "Try the new Vivideo" slot above V2App: switch/barHost.tsx once the switch's network half is loaded (it
 * shows the bar only when the switch is visible, and on a V2 render after V3 on this device sets Amplitude's
 * `ui_version` back to "v2" once, QA-A A-6). Nothing on a build without the V3 API.
 */
export function SwitchBarSlot() {
  const Bar = useSwitchBar();
  return Bar ? <Bar /> : null;
}
